// Self-serve billing, the account side (docs/specs/self-serve-billing.md section 5, stories P20
// and P21): payment methods, billing details and invoice emails, invoices. Types mirror
// `vd_api_types::billing` (the OpenAPI snapshot docs/api/openapi.json); every route is under
// `/v1/admin/billing`, admins only. The invoice PDF and receipt, and a bank account's
// microdeposit page, are 302 redirects to a URL the server reads fresh from Stripe: the console
// opens them as plain links in a new tab.

import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { API_BASE } from "../config";
import { billingKeys } from "./billingPlan";
import { api } from "./client";

// Payment methods ---------------------------------------------------------------------------------

export type PaymentMethodState = "ready" | "verifying" | "failed";

/** One payment method on file. */
export interface PaymentMethodView {
  /** Stripe's id (`pm_...`). */
  id: string;
  /** `card` or `us_bank_account`. */
  kind: string;
  /** The card brand or the bank's name. */
  label: string | null;
  last4: string | null;
  exp_month: number | null;
  exp_year: number | null;
  state: PaymentMethodState;
  is_default: boolean;
}

/** `GET /payment-methods`: the default first. */
export interface PaymentMethodList {
  methods: PaymentMethodView[];
}

/** 409 on remove: the only ready method while a subscription is active. */
export const LAST_PAYMENT_METHOD = "last_payment_method";

/** 409 on Pay now: no default payment method to pay with. */
export const NO_PAYMENT_METHOD = "no_payment_method";

// Billing details -------------------------------------------------------------------------------

/** A US billing address. */
export interface BillingAddress {
  line1: string | null;
  line2: string | null;
  city: string | null;
  /** Two-letter state code. */
  state: string | null;
  postal_code: string | null;
  /** `US`. */
  country: string;
}

/** `GET /details`. */
export interface BillingDetailsView {
  legal_name: string | null;
  address: BillingAddress;
  /** EIN, `12-3456789`. */
  tax_id: string | null;
  /** Invoice emails in order; the first is the Stripe customer email. */
  recipients: string[];
  /** Stripe has the latest saved version. */
  synced: boolean;
}

/** `PUT /details`. */
export interface PutBillingDetails {
  legal_name: string | null;
  address: BillingAddress;
  tax_id: string | null;
  /** 1 to 5, unique ignoring case. */
  recipients: string[];
}

/** At most this many invoice emails (`vd_core::billing_sync::MAX_RECIPIENTS`). */
export const MAX_RECIPIENTS = 5;

// Invoices --------------------------------------------------------------------------------------

export type InvoiceStatus = "draft" | "open" | "paid" | "void" | "uncollectible";
export type InvoicePaymentState = "processing" | "failed" | "action_required";
export type InvoiceKind = "subscription" | "usage";

/** One invoice. */
export interface InvoiceView {
  /** Stripe's id (`in_...`). */
  id: string;
  number: string | null;
  status: InvoiceStatus;
  payment_state: InvoicePaymentState | null;
  kind: InvoiceKind;
  total_cents: number;
  amount_due_cents: number;
  amount_paid_cents: number;
  amount_remaining_cents: number;
  currency: string;
  period_start: string | null;
  period_end: string | null;
  created_at: string;
  paid_at: string | null;
  attempt_count: number;
  next_attempt_at: string | null;
}

/** `GET /invoices`: newest first. */
export interface InvoicePage {
  invoices: InvoiceView[];
  next_cursor: string | null;
}

export const INVOICE_PAGE = 25;

// Routes ----------------------------------------------------------------------------------------

const BASE = "/v1/admin/billing";
const enc = encodeURIComponent;

export const accountKeys = {
  methods: [...billingKeys.all, "payment-methods"] as const,
  details: [...billingKeys.all, "details"] as const,
  invoices: [...billingKeys.all, "invoices"] as const,
};

export const billingAccount = {
  methods: () => api<PaymentMethodList>(`${BASE}/payment-methods`),
  /** Pay now: queues a payment of an open or failed invoice with the default method (D20). */
  pay: (id: string) => api<void>(`${BASE}/invoices/${enc(id)}/pay`, { method: "POST" }),
  makeDefault: (id: string) => api<void>(`${BASE}/payment-methods/${enc(id)}/default`, { method: "POST" }),
  remove: (id: string) => api<void>(`${BASE}/payment-methods/${enc(id)}`, { method: "DELETE" }),
  details: () => api<BillingDetailsView>(`${BASE}/details`),
  saveDetails: (body: PutBillingDetails) =>
    api<BillingDetailsView>(`${BASE}/details`, { method: "PUT", body }),
  invoices: (filter: InvoiceFilter, cursor?: string | null) =>
    api<InvoicePage>(`${BASE}/invoices`, {
      query: {
        cursor: cursor ?? undefined,
        limit: INVOICE_PAGE,
        status: filter === "all" ? undefined : filter,
      },
    }),
};

/** The invoice PDF: a 302 to Stripe, opened in a new tab. */
export function invoicePdfUrl(id: string): string {
  return `${API_BASE}${BASE}/invoices/${enc(id)}/pdf`;
}

/** The receipt of a paid invoice: a 302 to Stripe, opened in a new tab. */
export function invoiceReceiptUrl(id: string): string {
  return `${API_BASE}${BASE}/invoices/${enc(id)}/receipt`;
}

/** Stripe's microdeposit page for a bank account waiting on verification: a 302, new tab. */
export function verifyBankUrl(id: string): string {
  return `${API_BASE}${BASE}/payment-methods/${enc(id)}/verify`;
}

/** Admins only: the caller checks the role before using these. */
export const paymentMethodsQuery = queryOptions({
  queryKey: accountKeys.methods,
  queryFn: billingAccount.methods,
  staleTime: 15_000,
});

export const billingDetailsQuery = queryOptions({
  queryKey: accountKeys.details,
  queryFn: billingAccount.details,
  staleTime: 30_000,
});

/** One list per filter chip: the server filters (`status=paid|unpaid`), so every page counts. */
export const invoicesQuery = (filter: InvoiceFilter) =>
  infiniteQueryOptions({
    queryKey: [...accountKeys.invoices, filter],
    queryFn: ({ pageParam }) => billingAccount.invoices(filter, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    staleTime: 30_000,
  });

// Payment method lines ----------------------------------------------------------------------------

/** The card's expiry has passed (it is good through the end of its month). */
export function expired(pm: Pick<PaymentMethodView, "exp_month" | "exp_year">, now = new Date()): boolean {
  if (pm.exp_month === null || pm.exp_year === null) return false;
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth() + 1;
  return pm.exp_year < y || (pm.exp_year === y && pm.exp_month < m);
}

/** The caption under the name: "Expires 08/2028", or "Bank account". */
export function methodSub(pm: PaymentMethodView): string {
  if (pm.kind !== "card") return "Bank account";
  if (pm.exp_month === null || pm.exp_year === null) return "Card";
  return `Expires ${String(pm.exp_month).padStart(2, "0")}/${pm.exp_year}`;
}

// Invoice lines -----------------------------------------------------------------------------------

export type InvoiceFilter = "all" | "paid" | "unpaid";

/** The badge an invoice shows: label and tone (DESIGN.md "Billing (console)", Invoices tab). */
export function invoiceBadge(inv: Pick<InvoiceView, "status" | "payment_state">): {
  label: "Paid" | "Processing" | "Failed" | "Open" | "Void";
  tone: "success" | "neutral" | "danger" | "outline";
} {
  switch (inv.status) {
    case "paid":
      return { label: "Paid", tone: "success" };
    case "void":
      return { label: "Void", tone: "outline" };
    case "uncollectible":
      return { label: "Failed", tone: "danger" };
    default:
      if (inv.payment_state === "processing") return { label: "Processing", tone: "neutral" };
      if (inv.payment_state === "failed") return { label: "Failed", tone: "danger" };
      return { label: "Open", tone: "neutral" };
  }
}

/** A Failed or Open invoice offers Pay now. */
export function payable(inv: Pick<InvoiceView, "status" | "payment_state">): boolean {
  const { label } = invoiceBadge(inv);
  return inv.status !== "draft" && (label === "Failed" || label === "Open");
}

/**
 * A default method that can be charged: ready (not a bank account still verifying) and, for a
 * card, not past its expiry.
 */
export function workingDefault(methods: readonly PaymentMethodView[] | undefined): PaymentMethodView | null {
  const pm = methods?.find((m) => m.is_default);
  if (pm?.state !== "ready") return null;
  return pm.kind === "card" && expired(pm) ? null : pm;
}

/** Whether an invoice passes a filter chip. Unpaid: anything still owed. */
export function invoiceMatches(inv: Pick<InvoiceView, "status">, f: InvoiceFilter): boolean {
  if (f === "paid") return inv.status === "paid";
  if (f === "unpaid")
    return inv.status === "open" || inv.status === "uncollectible" || inv.status === "draft";
  return true;
}

const DAY = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
const DAY_YEAR = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
});
const MONTH = new Intl.DateTimeFormat("en-US", { month: "long", timeZone: "UTC" });

/** "Nov 1", with the year when it is not this year ("Dec 1, 2025"). */
export function invoiceDay(iso: string, now = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.getUTCFullYear() === now.getUTCFullYear() ? DAY.format(d) : DAY_YEAR.format(d);
}

/** "Nov 1 to Dec 1", or "October usage" for a usage invoice. */
export function invoicePeriod(
  inv: Pick<InvoiceView, "kind" | "period_start" | "period_end">,
  now = new Date(),
): string {
  if (inv.kind === "usage") {
    const d = inv.period_start ? new Date(inv.period_start) : null;
    return d && !Number.isNaN(d.getTime()) ? `${MONTH.format(d)} usage` : "Usage";
  }
  if (!inv.period_start || !inv.period_end) return "";
  return `${invoiceDay(inv.period_start, now)} to ${invoiceDay(inv.period_end, now)}`;
}

/** "$1,331.00" in the invoice's currency. */
export function invoiceAmount(inv: Pick<InvoiceView, "total_cents" | "currency">): string {
  const currency = (inv.currency || "usd").toUpperCase();
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(inv.total_cents / 100);
  } catch {
    return (inv.total_cents / 100).toFixed(2);
  }
}

// Invoice emails ----------------------------------------------------------------------------------

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * The inline error under each invoice email, in order (null when fine): "Enter an email
 * address." for a blank or malformed one, "Already on the list." for a repeat (ignoring case).
 */
export function recipientErrors(list: readonly string[]): (string | null)[] {
  const seen = new Set<string>();
  return list.map((raw) => {
    const e = raw.trim();
    if (!EMAIL.test(e)) return "Enter an email address.";
    const k = e.toLowerCase();
    if (seen.has(k)) return "Already on the list.";
    seen.add(k);
    return null;
  });
}
